import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import { useLayoutPropsUtil } from "../../internal/hooks";
import styles from "./Grid.module.scss";
import { GridSpecificProps } from "./internal/GridSpecificProps";
import { GridItem } from "./internal/GridItem";

/**
 * Props for the Grid component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends GridSpecificProps
 */
export type GridProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Applies `display: inline-grid` instead of `grid`
   */
  inline?: boolean;
} & GridSpecificProps & {
    /**
     * Responsive props for small screens (sm breakpoint).
     */
    sm?: GridSpecificProps;
    /**
     * Responsive props for medium screens (md breakpoint).
     */
    md?: GridSpecificProps;
    /**
     * Responsive props for large screens (lg breakpoint).
     */
    lg?: GridSpecificProps;
    /**
     * Responsive props for extra large screens (xl breakpoint).
     */
    xl?: GridSpecificProps;
    /**
     * Responsive props for extra extra large screens (xxl breakpoint).
     */
    xxl?: GridSpecificProps;
  };

/**
 * Grid component for creating CSS Grid layouts with responsive design.
 *
 * Features:
 * - CSS Grid layout with full control over grid properties
 * - Support for both grid and inline-grid display modes
 * - Responsive design with breakpoint-specific props (sm, md, lg, xl, xxl)
 * - Comprehensive layout utilities for spacing, alignment, and positioning
 * - Template columns, rows, and areas configuration
 * - Auto columns and rows sizing
 * - Automatic style generation and class management
 * - Support for all standard div element props
 * - Flexible grid template and auto-sizing controls
 * - Automatic tracking ID generation for analytics
 * - Compound component structure with Item sub-component
 *
 * @example
 * <Grid templateColumns="repeat(3, 1fr)" gap="4">
 *   <Grid.Item>Grid Item 1</Grid.Item>
 *   <Grid.Item>Grid Item 2</Grid.Item>
 *   <Grid.Item>Grid Item 3</Grid.Item>
 * </Grid>
 *
 * @example
 * <Grid
 *   templateColumns="repeat(1, 1fr)"
 *   gap="2"
 *   md={{ templateColumns: "repeat(3, 1fr)", gap: "4" }}
 * >
 *   <Grid.Item>Grid Item 1</Grid.Item>
 *   <Grid.Item>Grid Item 2</Grid.Item>
 *   <Grid.Item>Grid Item 3</Grid.Item>
 * </Grid>
 */
export const Grid = Object.assign(
  forwardRef<HTMLDivElement, GridProps>(function GridInner(props, ref) {
    const {
      inline,
      templateColumns,
      templateRows,
      templateAreas,
      autoColumns,
      autoRows,
      sm,
      md,
      lg,
      xl,
      xxl,
      ...nonLayoutProps
    } = props;
    const combineLayoutProps = {
      ...nonLayoutProps,
      gridTemplateColumns: props.templateColumns,
      gridTemplateRows: props.templateRows,
      gridTemplateAreas: props.templateAreas,
      gridAutoColumns: props.autoColumns,
      gridAutoRows: props.autoRows,
      sm: {
        ...sm,
        gridTemplateColumns: sm?.templateColumns,
        gridTemplateRows: sm?.templateRows,
        gridTemplateAreas: sm?.templateAreas,
        gridAutoColumns: sm?.autoColumns,
        gridAutoRows: sm?.autoRows,
      },
      md: {
        ...md,
        gridTemplateColumns: md?.templateColumns,
        gridTemplateRows: md?.templateRows,
        gridTemplateAreas: md?.templateAreas,
        gridAutoColumns: md?.autoColumns,
        gridAutoRows: md?.autoRows,
      },
      lg: {
        ...lg,
        gridTemplateColumns: lg?.templateColumns,
        gridTemplateRows: lg?.templateRows,
        gridTemplateAreas: lg?.templateAreas,
        gridAutoColumns: lg?.autoColumns,
        gridAutoRows: lg?.autoRows,
      },
      xl: {
        ...xl,
        gridTemplateColumns: xl?.templateColumns,
        gridTemplateRows: xl?.templateRows,
        gridTemplateAreas: xl?.templateAreas,
        gridAutoColumns: xl?.autoColumns,
        gridAutoRows: xl?.autoRows,
      },
      xxl: {
        ...xxl,
        gridTemplateColumns: xxl?.templateColumns,
        gridTemplateRows: xxl?.templateRows,
        gridTemplateAreas: xxl?.templateAreas,
        gridAutoColumns: xxl?.autoColumns,
        gridAutoRows: xxl?.autoRows,
      },
    };
    const { layoutStyles, componentProps } =
      useLayoutPropsUtil(combineLayoutProps);
    const { children, className, style, ...rest } = componentProps;

    const gridClassNames = cx(className, {
      [styles["grid"]]: !inline,
      [styles["inline-grid"]]: inline,
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <div
        className={gridClassNames}
        data-anv="grid"
        style={styleCombined}
        ref={ref}
        {...rest}
      >
        {children}
      </div>
    );
  }),
  {
    /**
     * GridItem component for individual items within a CSS Grid layout.
     *
     * Features:
     * - Individual grid item with full layout utility support
     * - Automatic grid item styling and class management
     * - Comprehensive layout utilities for positioning and sizing
     * - Support for all standard div element props
     * - Flexible grid positioning and sizing controls
     * - Automatic style generation and class management
     * - Works seamlessly with Grid component
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Grid templateColumns="repeat(2, 1fr)" gap="4">
     *   <Grid.Item>Grid Item 1</Grid.Item>
     *   <Grid.Item>Grid Item 2</Grid.Item>
     * </Grid>
     */
    Item: GridItem,
  },
);
Grid.displayName = "Grid";
